import {
  Modal,
  Paper,
  IconButton,
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  Fade,
} from "@mui/material";
import { Close, CheckCircle } from "@mui/icons-material";
import { useState } from "react";

function ReviewModal({ open, orderId, onClose, onSubmit }) {
  const [reviewData, setReviewData] = useState({
    rating: 0,
    review: "",
    submitted: false,
  });

  const handleRatingChange = (event, newValue) => {
    setReviewData({ ...reviewData, rating: newValue });
  };

  const handleReviewChange = (event) => {
    setReviewData({ ...reviewData, review: event.target.value });
  };

  const handleSubmit = () => {
    onSubmit({
      rating: reviewData.rating,
      review: reviewData.review,
    });
    
    // Show thank you animation
    setReviewData(prev => ({ ...prev, submitted: true }));
    
    // Auto close after 3 seconds
    setTimeout(() => {
      handleClose();
    }, 3000);
  };

  const handleClose = () => {
    setReviewData({
      rating: 0,
      review: "",
      submitted: false,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: 500,
          p: { xs: 2, sm: 3 },
          mx: "auto",
          position: "relative",
          bgcolor: '#f7f4f0'
        }}
      >
        
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.secondary",
          }}
        >
          <Close />
        </IconButton>

        
        {!reviewData.submitted ? (
          <Fade in={!reviewData.submitted}>
            <Box>
              <Box sx={{ textAlign: "center", mb: 3, mt: 2 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Rate Your Order
                </Typography>
                <Rating
                  value={reviewData.rating}
                  onChange={handleRatingChange}
                  size="large"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                    "& .MuiRating-iconFilled": {
                      color: "#FFD700",
                    },
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {reviewData.rating > 0
                    ? `You rated ${reviewData.rating} star${reviewData.rating > 1 ? "s" : ""}`
                    : "Select your rating"}
                </Typography>
              </Box>

              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Write Your Review
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Share your thoughts about the product quality, delivery experience, and overall satisfaction..."
                  value={reviewData.review}
                  onChange={handleReviewChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "black",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "black",
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  onClick={handleSubmit}
                  variant="outlined"
                //   disabled={reviewData.rating === 0}
                  sx={{
                    color: "black",
                    borderColor: "black",
                    borderRadius: "24px",
                    textTransform: "none",
                    px: 3,
                    "&:hover": {
                      bgcolor: "black",
                      color: "white",
                    },
                    "&:disabled": {
                      borderColor: "#ccc",
                      color: "#ccc",
                    },
                  }}
                >
                  Submit Review
                </Button>
              </Box>
            </Box>
          </Fade>
        ) : (
          <Fade in={reviewData.submitted}>
            <Box sx={{ textAlign: "center", py: 4 }}>
              {/* Animated Checkmark */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'black',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  animation: 'scaleIn 0.5s ease-out',
                  '@keyframes scaleIn': {
                    '0%': {
                      transform: 'scale(0)',
                      opacity: 0,
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <CheckCircle 
                  sx={{ 
                    fontSize: 40, 
                    bgcolor: "black",
                    color: 'white',
                    animation: 'checkmark 0.3s ease-out 0.2s both',
                    '@keyframes checkmark': {
                      '0%': {
                        transform: 'scale(0) rotate(-45deg)',
                        opacity: 0,
                      },
                      '100%': {
                        transform: 'scale(1) rotate(0deg)',
                        opacity: 1,
                      },
                    },
                  }} 
                />
              </Box>

              {/* Thank You Message */}
              <Typography 
                variant="h4" 
                fontWeight={700} 
                gutterBottom
                sx={{
                  background: 'linear-gradient(45deg, #000000, #333333)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  animation: 'fadeInUp 0.5s ease-out 0.3s both',
                  '@keyframes fadeInUp': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(20px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                Thank You!
              </Typography>
              
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  mb: 2,
                  animation: 'fadeInUp 0.5s ease-out 0.4s both',
                }}
              >
                Your review has been submitted
              </Typography>

              {/* Pulsating dots */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                {[0, 1, 2].map((dot) => (
                  <Box
                    key={dot}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'black',
                      animation: `pulse 1.4s ease-in-out ${dot * 0.2}s infinite both`,
                      '@keyframes pulse': {
                        '0%, 80%, 100%': {
                          transform: 'scale(0.8)',
                          opacity: 0.5,
                        },
                        '40%': {
                          transform: 'scale(1)',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Fade>
        )}
      </Paper>
    </Modal>
  );
}

export default ReviewModal;